import { useCallback, useEffect } from "react";
import { MetricEvents } from "@shared/integrations/delivery_keys";
import type { ScorecardKey } from "@shared/scorecard";
import { logMetricEvent } from "../lib/delivery";
import { useForegroundStopwatch } from "./useForegroundStopwatch";

export interface AnnotationFieldTimerParams {
  /** URL of the document being annotated, or null while not loaded. */
  docUrl: string | null;
  /** Scorecard field currently under review, or null while not loaded. */
  field: ScorecardKey | null;
  /** True while a document is being fetched. Timer starts once loading completes. */
  loading: boolean;
}

export interface AnnotationFieldTimerHandle {
  /**
   * Stop the timer and emit corpus.field.annotated with the elapsed foreground duration.
   * Call at the moment the human commits: before the async save, after the decision is made.
   */
  record: (labels: string[], state: string) => void;
}

/**
 * Tracks how long an annotator spends on a single (document, field) pair and fires
 * corpus.field.annotated when they commit. Foreground-only: time with the tab hidden is excluded.
 * Start is automatic — tied to when loading resolves with an active document and field.
 */
export function useAnnotationFieldTimer({
  docUrl,
  field,
  loading,
}: AnnotationFieldTimerParams): AnnotationFieldTimerHandle {
  const stopwatch = useForegroundStopwatch();

  useEffect(() => {
    if (!loading && docUrl && field !== null) {
      stopwatch.reset();
    }
  }, [loading, docUrl, field, stopwatch]);

  const record = useCallback(
    (labels: string[], state: string) => {
      const durationMs = stopwatch.stop();
      if (!docUrl || !field) return;
      logMetricEvent(MetricEvents.corpusFieldAnnotated, {
        value: durationMs,
        metadata: { doc_url: docUrl, field, labels: labels.join(","), state },
      });
    },
    [docUrl, field, stopwatch]
  );

  return { record };
}
